import React, { useEffect, useState } from 'react';
import Modal from './ui/Modal';
import { Link } from 'lucide-react';
import apiService from '../services/apiService';
import { useI18n } from '../utils/i18n';
import { ticketPasstZurSuche } from '../utils/suchFilter';
import { useFeature } from '../utils/featuresCache';
import { useToast } from './ToastProvider';

const TicketMergeModal = ({ isOpen, sourceTicket, onClose, onMerged }) => {
  const { t } = useI18n();
  const kundenAktiv = useFeature('customers'); // #251: Platzhalter nennt Kunde nur, wenn der Kundenstamm an ist
  const { pushToast } = useToast();
  const [openTickets, setOpenTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterText, setFilterText] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [selectedTargetId, setSelectedTargetId] = useState(null);

  useEffect(() => {
    const load = async () => {
      if (!isOpen) return;
      setLoading(true);
      try {
        const ticketsResponse = await apiService.getOpenTickets();
        setOpenTickets((ticketsResponse?.tickets || []).filter(t => t.id !== sourceTicket?.id));
      } catch (e) {
        console.error('Error loading open tickets:', e);
        setOpenTickets([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [isOpen, sourceTicket?.id]);

  // #251: Nummer, Titel, Kunde, Ansprechpartner -- gemeinsamer Helfer. Das
  // ist der Dialog aus der Meldung („Ticket einem anderen Ticket zuordnen“).
  const filteredTickets = openTickets.filter(ticket => ticketPasstZurSuche(ticket, filterText));

  const handleMerge = async (targetTicketId) => {
    if (!sourceTicket?.id || !targetTicketId) return;
    if (sourceTicket.id === targetTicketId) return;
    setSelectedTargetId(targetTicketId);
    setActionLoading(true);
    try {
      await apiService.mergeTickets(sourceTicket.id, targetTicketId);
      if (onMerged) await onMerged();
      if (onClose) onClose();
    } catch (e) {
      console.error('Merge tickets failed:', e);
      pushToast({ type: 'error', message: t('ticket.merge.error', 'Fehler beim Zusammenführen der Tickets.') });
    } finally {
      setActionLoading(false);
      setSelectedTargetId(null);
    }
  };

  if (!isOpen) return null;

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      title={<><Link size={24} /><h3>{t('ticket.addToExisting', 'Zu vorhandenem Ticket hinzufügen:')}</h3></>}
      ariaLabel={t('ticket.addToExisting', 'Zu vorhandenem Ticket hinzufügen:')}
      maxWidth="600px"
      footer={<button type="button" onClick={onClose} className="btn btn-secondary">{t('common.cancel', 'Abbrechen')}</button>}
    >

        <div className="ticket-selector">
          <div className="ticket-filter" style={{ marginBottom: 12 }}>
            <input
              type="text"
              placeholder={(kundenAktiv ? t('ticket.filter.placeholderCustomer', 'Nach Ticketnummer, Titel, Kunde oder Ansprechpartner suchen...') : t('ticket.filter.placeholder', 'Nach Ticketnummer oder Titel suchen...'))}
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              className="ticket-filter-input"
              style={{ width: '100%' }}
            />
          </div>

          {loading ? (
            <p>{t('ticket.loading', 'Lade Tickets...')}</p>
          ) : openTickets.length === 0 ? (
            <p>{t('ticket.noneOpen', 'Keine offenen Tickets gefunden.')}</p>
          ) : filteredTickets.length === 0 ? (
            <p>{t('ticket.noneMatchFilter', 'Keine Tickets gefunden, die dem Filter entsprechen.')}</p>
          ) : (
            <div className="ticket-list-scrollable" style={{ maxHeight: 360, overflow: 'auto' }}>
              {filteredTickets.map(ticket => (
                <div key={ticket.id} className="ticket-item-compact" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid var(--color-border-faint)' }}>
                  <div className="ticket-info-compact" style={{ minWidth: 0 }}>
                    <div className="ticket-header-compact" style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <span className="ticket-number-compact">#{ticket.ticketNumber}</span>
                      <div className="ticket-badges-compact" style={{ display: 'flex', gap: 6 }}>
                        <span className={`status-badge-compact status-${ticket.status}`}>{ticket.status}</span>
                        <span className={`priority-badge-compact priority-${ticket.priority}`}>{ticket.priority}</span>
                      </div>
                    </div>
                    <div className="ticket-title-compact" style={{ whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden', maxWidth: 380 }}>{ticket.title}</div>
                  </div>
                  <button 
                    onClick={() => handleMerge(ticket.id)}
                    className="btn btn-secondary btn-compact"
                    disabled={actionLoading}
                  >
                    {actionLoading && selectedTargetId === ticket.id ? t('common.adding', 'Füge hinzu...') : t('common.add', 'Hinzufügen')}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
    </Modal>
  );
};

export default TicketMergeModal;


